import React, { useState, useEffect } from "react";
import { useAuth } from '../../../../context/AuthContext';
import { useTranslation } from "react-i18next";
import Swal from 'sweetalert2';
import axios from "axios";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../../../Helpers/apiConfig";

// Excel'den okunan satırdaki başlıkları (boşluk/büyük-küçük harf farkı olabilir) normalize eder.
const normalizeRow = (row) => {
    const normalized = {};
    Object.keys(row).forEach((key) => {
        normalized[key.toString().trim().toLowerCase()] = row[key];
    });
    return normalized;
};

// Seçilen excel dosyasını satır satır objeye çevirir.
const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                resolve(XLSX.utils.sheet_to_json(sheet, { defval: "" }));
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
};

function ManagerStudentCreate() {
    const { user } = useAuth();
    const { t } = useTranslation();

    // State Yönetimi
    const [classes, setClasses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isBulkLoading, setIsBulkLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);

    // Tekli Öğrenci Form State
    const [formData, setFormData] = useState({
        username: "",
        fullName: "",
        password: "",
        classId: ""
    });

    // Toplu Ekleme Form State
    const [bulkClassId, setBulkClassId] = useState("");

    // Input değişikliklerini yakalayan fonksiyon
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Sınıfları API'den Çekme
    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/manager/EditSchool/GetClasses`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`
                    }
                });

                if (response.status === 200 && response.data.success) {
                    setClasses(response.data.data);
                } else {
                    Swal.fire("Hata", response.data.message || "Sınıflar yüklenemedi.", "error");
                }
            } catch (err) {
                Swal.fire("Hata", "Sınıflar yüklenirken beklenmedik bir hata oluştu.", "error");
            } finally {
                setIsFetching(false);
            }
        };

        fetchClasses();
    }, [user.token]); // Token değişirse yeniden tetiklenir

    // Tekli Öğrenci Kayıt İşlemi
    const onSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Seçili sınıfı Array içerisinden buluyoruz
        const selectedClass = classes.find(c => c.id.toString() === formData.classId.toString());

        if (!selectedClass) {
            Swal.fire("Uyarı", "Lütfen geçerli bir sınıf seçiniz.", "warning");
            setIsLoading(false);
            return;
        }

        try {
            const payload = {
                username: formData.username,
                password: formData.password,
                fullName: formData.fullName,
                roleId: 2,
                schoolId: selectedClass.schoolId,
                classId: selectedClass.id,
                sectionId: selectedClass.sectionId,
            };

            const response = await axios.post(
                `${API_BASE_URL}/manager/EditSchool/CreateStudent`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`
                    }
                }
            );

            if (response.status === 200 && response.data.success) {
                Swal.fire({
                    title: "Başarılı!",
                    text: "Öğrenci başarıyla oluşturuldu.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false
                });

                // Formu temizle (Sayfa yenilemeye gerek kalmaz)
                setFormData({ username: "", fullName: "", password: "", classId: "" });
            } else {
                Swal.fire("Hata", response.data.message || "Kayıt başarısız.", "error");
            }
        } catch (err) {
            Swal.fire("Hata", "İşlem sırasında bir hata oluştu.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    // Toplu Öğrenci Kayıt İşlemi (Excel -> foreach -> tekli create endpoint)
    const onBulkSubmit = async (e) => {
        e.preventDefault();

        const selectedClass = classes.find(c => c.id.toString() === bulkClassId.toString());
        if (!selectedClass) {
            Swal.fire("Uyarı", "Lütfen geçerli bir sınıf seçiniz.", "warning");
            return;
        }

        const fileInput = document.getElementById("excelFile");
        const file = fileInput.files[0];
        if (!file) {
            Swal.fire("Uyarı", "Lütfen bir excel dosyası seçiniz.", "warning");
            return;
        }

        setIsBulkLoading(true);

        let rawRows;
        try {
            rawRows = await readExcelFile(file);
        } catch (err) {
            Swal.fire("Hata", "Excel dosyası okunamadı.", "error");
            setIsBulkLoading(false);
            return;
        }

        const rows = rawRows.map(normalizeRow).filter((row) => row["ad soyad"] || row["email"]);

        if (rows.length === 0) {
            Swal.fire("Uyarı", "Excel dosyasında kayıt bulunamadı.", "warning");
            setIsBulkLoading(false);
            return;
        }

        let successCount = 0;
        const failed = [];

        Swal.fire({
            title: "Öğrenciler Oluşturuluyor",
            html: `İşleniyor: 0 / ${rows.length}`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const fullName = (row["ad soyad"] || "").toString().trim();
            const email = (row["email"] || "").toString().trim();
            let password = (row["şifre"] || "").toString().trim();
            if (!password) {
                password = fullName.split(" ")[0] || "";
            }

            Swal.update({
                html: `İşleniyor: ${i + 1} / ${rows.length}<br/><small>${email || "(boş satır)"}</small>`
            });

            if (!fullName || !email) {
                failed.push({ email: email || "(boş)", reason: "Ad soyad veya email eksik" });
                continue;
            }

            try {
                const payload = {
                    username: email,
                    password: password,
                    fullName: fullName,
                    roleId: 2,
                    schoolId: selectedClass.schoolId,
                    classId: selectedClass.id,
                    sectionId: selectedClass.sectionId,
                };

                const response = await axios.post(
                    `${API_BASE_URL}/manager/EditSchool/CreateStudent`,
                    payload,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.token}`
                        }
                    }
                );
                if (response.status === 200 && response.data.success) {
                    successCount++;
                } else {
                    failed.push({ email, reason: response.data.message || "Bilinmeyen hata" });
                }
            } catch (err) {
                failed.push({ email, reason: "İstek başarısız oldu" });
            }
        }

        Swal.fire({
            title: failed.length === 0 ? "Başarılı!" : "Tamamlandı",
            icon: failed.length === 0 ? "success" : "warning",
            html: `
                <p>${successCount} / ${rows.length} öğrenci başarıyla oluşturuldu.</p>
                ${failed.length > 0
                    ? `<div style="text-align:left; max-height:200px; overflow-y:auto;">
                         <b>Başarısız Olanlar:</b>
                         <ul>${failed.map(f => `<li>${f.email} - ${f.reason}</li>`).join("")}</ul>
                       </div>`
                    : ""
                }
            `
        });

        fileInput.value = "";
        setIsBulkLoading(false);
    };

    return (
        <div className="container-fluid py-4">
            <div className="row justify-content-center">
                {/* Sol Taraf - Tekli Öğrenci Oluşturma */}
                <div className="col-lg-6 col-md-12 mb-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
                            <h4 className="text-center text-primary fw-bold">{t("createstudent")}</h4>
                        </div>
                        <div className="card-body">
                            {isFetching ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Yükleniyor...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Sınıflar yükleniyor...</p>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="username" className="form-label fw-semibold">{t('userEmail')}</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="username"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            placeholder="ornek@ogrenci.com"
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="fullName" className="form-label fw-semibold">{t('namesurname')}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="fullName"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleChange}
                                            placeholder="Ad Soyad"
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="password" className="form-label fw-semibold">{t('password')}</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="******"
                                            required
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label htmlFor="classId" className="form-label fw-semibold">{t('className')}</label>
                                        <select
                                            id="classId"
                                            name="classId"
                                            className="form-select"
                                            value={formData.classId}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="" disabled>{t('pleaseSelectClass')}</option>
                                            {classes.map((dt) => (
                                                <option key={dt.id} value={dt.id}>{dt.className}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-primary py-2" disabled={isLoading}>
                                            {isLoading ? (
                                                <span><i className="spinner-border spinner-border-sm me-2"></i> Kaydediliyor...</span>
                                            ) : (
                                                t('create')
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sağ Taraf - Toplu Öğrenci Oluşturma */}
                <div className="col-lg-6 col-md-12 mb-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
                            <h4 className="text-center text-success fw-bold">Toplu Oluşturma</h4>
                        </div>
                        <div className="card-body">
                            <div className="alert alert-info border-0 shadow-sm" role="alert">
                                <h6 className="alert-heading fw-bold mb-1"><i className="bi bi-info-circle me-2"></i>Bilgi</h6>
                                <p className="mb-0 small">Excel yükleyerek toplu olarak öğrenci kaydedebilirsiniz.</p>
                            </div>

                            <div className="bg-light p-3 rounded mb-4 border">
                                <h6 className="fw-bold">Kullanım Talimatı:</h6>
                                <ul className="small text-muted mb-0">
                                    <li>Şablon excel'i indirip aynı formatta doldurunuz.</li>
                                    <li>Öğrencinin Adı Soyadı için <b>"ad soyad"</b> başlıklı bir sütun olmalıdır.</li>
                                    <li>Email için <b>"email"</b> başlıklı bir sütun olmalıdır.</li>
                                    <li>Şifre için <b>"şifre"</b> başlıklı bir sütun olmalıdır.</li>
                                    <li>Şifre sütunu boş bırakılırsa, ad soyad'ın ilk kelimesi otomatik şifre olarak atanır. Örn: <b>"ahmet berk açıksarı"</b> için şifre <b>"ahmet"</b> olur.</li>
                                </ul>
                                <a
                                    href="/topluogrencikaydet.xlsx"
                                    download
                                    className="btn btn-outline-success btn-sm mt-3"
                                >
                                    <i className="bi bi-download me-1"></i>Örnek Excel Şablonunu İndir
                                </a>
                            </div>

                            <form onSubmit={onBulkSubmit}>
                                <div className="mb-4">
                                    <label htmlFor="selectBulkClass" className="form-label fw-semibold">Hedef Sınıf</label>
                                    <select
                                        id="selectBulkClass"
                                        className="form-select"
                                        value={bulkClassId}
                                        onChange={(e) => setBulkClassId(e.target.value)}
                                        required
                                    >
                                        <option value="" disabled>Sınıf Seçiniz</option>
                                        {/* Burayı API'den gelen verilerle dinamik yapıyoruz */}
                                        {classes.map((dt) => (
                                            <option key={dt.id} value={dt.id}>{dt.className}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="excelFile" className="form-label fw-semibold">Excel Dosyası</label>
                                    <input className="form-control" type="file" id="excelFile" accept=".xlsx, .xls, .csv" disabled={isBulkLoading} />
                                </div>

                                <div className="d-grid">
                                    <button type="submit" className="btn btn-success py-2" disabled={isBulkLoading}>
                                        {isBulkLoading ? (
                                            <span><i className="spinner-border spinner-border-sm me-2"></i> Oluşturuluyor...</span>
                                        ) : (
                                            `${t("create")} (Toplu)`
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ManagerStudentCreate;